// Who is signed in and where they are (world, city). Tokens never come here — they live in the Worker's cookie.
// The chosen world and city are remembered per account in localStorage so the app reopens where it was.
import { create } from "zustand";

export type SessionStatus = "unknown" | "out" | "in";

interface Remembered {
  wid?: number;
  vid?: number;
}

interface SessionState {
  status: SessionStatus;
  uid: number | null;
  login: string | null;
  email: string | null;
  wid: number | null;
  vid: number | null;
  worldNames: Record<string, string>;
  setSignedIn: (p: { uid: number; login?: string | null; email?: string | null }) => void;
  setSignedOut: () => void;
  setWorld: (wid: number | null) => void;
  setVillage: (vid: number | null) => void;
  setWorldNames: (names: Record<string, string>) => void;
}

const KEY = "valor-web:place";

function remembered(uid: number | null): Remembered {
  if (!uid) return {};
  try {
    const all = JSON.parse(localStorage.getItem(KEY) || "{}") as Record<string, Remembered>;
    return all[String(uid)] ?? {};
  } catch {
    return {};
  }
}

function remember(uid: number | null, patch: Remembered) {
  if (!uid) return;
  try {
    const all = JSON.parse(localStorage.getItem(KEY) || "{}") as Record<string, Remembered>;
    all[String(uid)] = { ...(all[String(uid)] ?? {}), ...patch };
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* storage may be unavailable (private mode): the app still works, it just forgets the place */
  }
}

export const useSession = create<SessionState>((set, get) => ({
  status: "unknown",
  uid: null,
  login: null,
  email: null,
  wid: null,
  vid: null,
  worldNames: {},
  setSignedIn: ({ uid, login, email }) => {
    const r = remembered(uid);
    set({ status: "in", uid, login: login ?? null, email: email ?? null, wid: r.wid ?? null, vid: r.vid ?? null });
  },
  setSignedOut: () => set({ status: "out", uid: null, login: null, email: null, wid: null, vid: null }),
  setWorld: (wid) => {
    const { uid } = get();
    if (wid === get().wid) return;
    set({ wid, vid: null });
    remember(uid, { wid: wid ?? undefined, vid: undefined });
  },
  setVillage: (vid) => {
    const { uid } = get();
    set({ vid });
    remember(uid, { vid: vid ?? undefined });
  },
  setWorldNames: (worldNames) => set({ worldNames }),
}));

/** "World 2" — the game's world name when it has one, else the number. */
export function worldLabel(wid: number | null | undefined, names: Record<string, string> = useSession.getState().worldNames): string {
  if (!wid) return "All Worlds";
  const n = names[String(wid)];
  return n ? `World ${wid} · ${n}` : `World ${wid}`;
}

export const worldShort = (wid: number | null | undefined) => (wid ? `World ${wid}` : "All Worlds");
